import { CATEGORY_ICONS, fmt, hasValue, parseKip, parseQty } from '@k2pos/core';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddValueSheet } from '@/components/ui/AddValueSheet';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CategoryPill, UnitChip } from '@/components/ui/Chip';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Field as TextField } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { IconCheck, IconPlus } from '@/components/icons';
import { cx } from '@/lib/cx';
import { useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { useTemplates } from '@/hooks/useTemplates';
import { usePos, type NewProduct } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './AddProduct.module.css';

type Method = 'template' | 'rapid';

/**
 * Two of the three fast-entry paths from the handoff. The third — add-while-selling
 * — lives in the Pick screen, which routes here with `?name=` prefilled.
 */
export function AddProduct() {
  const [params] = useSearchParams();
  const prefill = params.get('name') ?? undefined;
  const goBack = useGoBack('/sell');
  const [method, setMethod] = useState<Method>(prefill ? 'rapid' : 'template');

  return (
    <>
      <AppBar title="ເພີ່ມສິນຄ້າ" onBack={goBack} />

      <div className={styles.tabs} role="tablist">
        <MethodTab
          label="ແມ່ແບບ"
          sub="ຕິກເອົາທີ່ຮ້ານມີ"
          selected={method === 'template'}
          onPress={() => setMethod('template')}
        />
        <MethodTab
          label="ພິມເອງ"
          sub="ເພີ່ມຕໍ່ກັນໄດ້"
          selected={method === 'rapid'}
          onPress={() => setMethod('rapid')}
        />
      </div>

      {method === 'template' ? <TemplateMethod /> : <RapidMethod prefill={prefill} />}
    </>
  );
}

function MethodTab({
  label,
  sub,
  selected,
  onPress,
}: {
  label: string;
  sub: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onPress}
      className={cx(styles.tab, selected && styles.tabOn)}
    >
      <Text weight={800} size={13} color={selected ? colors.surfaceAlt : colors.textMuted}>
        {label}
      </Text>
      <Text size={9.5} color={selected ? colors.onDarkBright : colors.textFaint}>
        {sub}
      </Text>
    </button>
  );
}

const ALL = 'ທັງໝົດ';

/** Method A — pre-seeded construction items, grouped by category. */
function TemplateMethod() {
  const navigate = useNavigate();
  const addProduct = usePos((s) => s.addProduct);
  const catalog = usePos((s) => s.catalog);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const [query, setQuery] = useState('');
  const [cat, setCat] = useState(ALL);
  /** On by default: mid-onboarding the owner cares about what is still missing. */
  const [hideExisting, setHideExisting] = useState(true);

  const owned = useMemo(() => new Set(catalog.map((p) => p.name)), [catalog]);

  /** From the back office when it can be reached, from the bundled list when not. */
  const { groups: templates } = useTemplates();

  /** Groups after search + hide-existing, before the category filter is applied,
   *  so the pill counts describe what picking that category would actually show. */
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.map((group) => ({
      cat: group.cat,
      items: group.items.filter(
        (item) =>
          (!q || item.name.toLowerCase().includes(q)) && !(hideExisting && owned.has(item.name)),
      ),
    })).filter((group) => group.items.length > 0);
  }, [templates, query, hideExisting, owned]);

  const visible = useMemo(
    () => (cat === ALL ? searched : searched.filter((group) => group.cat === cat)),
    [searched, cat],
  );

  const categories = useMemo<[string, number][]>(
    () => [
      [ALL, searched.reduce((sum, group) => sum + group.items.length, 0)],
      ...searched.map((group): [string, number] => [group.cat, group.items.length]),
    ],
    [searched],
  );

  const shown = visible.flatMap((group) => group.items);
  const allShownPicked = shown.length > 0 && shown.every((item) => picked.has(item.name));
  // A pick can be filtered out of view; the footer count still includes it, so say so.
  const hiddenPicks = picked.size - shown.filter((item) => picked.has(item.name)).length;

  /**
   * Each pick is its own durable write, so a failure part-way leaves the
   * products that did save. Sequential rather than parallel: the outbox is one
   * IndexedDB store and the order is the order the owner ticked them.
   */
  const commitAll = useMemo(
    () => async () => {
      for (const group of templates) {
        for (const item of group.items) {
          // Guard against a name added by another route while this was open.
          // `item` carries masterId/masterVersion when it came from the back
          // office, and addProduct records them — provenance has to be captured
          // at copy time or never.
          if (picked.has(item.name) && !owned.has(item.name)) await addProduct(item);
        }
      }
    },
    [templates, picked, owned, addProduct],
  );

  const { run, busy, error, dismiss } = useMoneyAction(commitAll, () => navigate(-1));

  function toggle(key: string) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  /** Tick or untick everything the current filter shows — the point of filtering. */
  function toggleShown() {
    setPicked((current) => {
      const next = new Set(current);
      for (const item of shown) {
        if (allShownPicked) next.delete(item.name);
        else next.add(item.name);
      }
      return next;
    });
  }

  return (
    <>
      <div className={styles.filters}>
        <div className={styles.search}>
          <TextField
            value={query}
            onChange={setQuery}
            placeholder="ຄົ້ນຫາໃນແມ່ແບບ..."
            label="ຄົ້ນຫາໃນແມ່ແບບ"
          />
        </div>

        <div className={cx('hscroll', styles.pills)} role="tablist">
          {categories.map(([name, count]) => (
            <CategoryPill
              key={name}
              name={name}
              icon={CATEGORY_ICONS[name] ?? 'grid'}
              count={count}
              selected={cat === name}
              onPress={() => setCat(name)}
            />
          ))}
        </div>

        <div className={styles.filterRow}>
          <button
            type="button"
            role="switch"
            aria-checked={hideExisting}
            onClick={() => setHideExisting((v) => !v)}
            className={cx(styles.toggle, hideExisting && styles.toggleOn)}
          >
            {hideExisting ? <IconCheck size={12} /> : null}
            <Text
              weight={700}
              size={11.5}
              color={hideExisting ? colors.surfaceAlt : colors.textMuted}
            >
              ເຊື່ອງທີ່ມີແລ້ວ
            </Text>
          </button>

          {shown.length > 0 ? (
            <button type="button" onClick={toggleShown} className={styles.selectAll}>
              <Text weight={700} size={11.5} color={colors.skyDeep}>
                {allShownPicked ? 'ບໍ່ເລືອກ' : `ເລືອກທັງໝົດ (${shown.length})`}
              </Text>
            </button>
          ) : null}
        </div>
      </div>

      <div className={cx('vscroll', styles.body)}>
        <ErrorBanner message={error} onDismiss={dismiss} />

        {visible.map((group) => (
          <div key={group.cat} className={styles.group}>
            <Text weight={800} size={11} color={colors.textMuted}>
              {group.cat}
            </Text>
            {group.items.map((item) => {
              const already = owned.has(item.name);
              const on = picked.has(item.name);
              return (
                <button
                  key={item.name}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  disabled={already}
                  onClick={() => toggle(item.name)}
                  className={styles.templateBtn}
                >
                  <Card className={cx(styles.templateRow, already && styles.rowMuted)}>
                    <span className={cx(styles.tick, on && styles.tickOn)}>
                      {on ? <IconCheck size={12} /> : null}
                    </span>
                    <div className="grow col">
                      <Text weight={700} size={13} align="left" lines={1}>
                        {item.name}
                      </Text>
                      <Mono size={10.5} color={colors.textMuted} align="left">
                        {fmt(item.price)} ₭ / {item.unit}
                        {already ? ' · ມີແລ້ວ' : ''}
                      </Mono>
                    </div>
                  </Card>
                </button>
              );
            })}
          </div>
        ))}

        {visible.length === 0 ? (
          <Text size={12.5} color={colors.textMuted} align="center" className={styles.empty}>
            {hideExisting && !query
              ? 'ເພີ່ມຄົບແລ້ວທຸກລາຍການໃນແມ່ແບບ'
              : 'ບໍ່ພົບໃນແມ່ແບບ · ລອງ “ພິມເອງ”'}
          </Text>
        ) : null}
      </div>

      <div className={styles.footer}>
        {hiddenPicks > 0 ? (
          <Text size={10.5} color={colors.textFaint} align="center" className={styles.hiddenNote}>
            ລວມ {hiddenPicks} ລາຍການທີ່ຖືກກັ່ນຕອງໄວ້
          </Text>
        ) : null}
        <Button
          label={busy ? 'ກຳລັງບັນທຶກ...' : `ເພີ່ມ ${picked.size} ລາຍການ`}
          disabled={picked.size === 0 || busy}
          onPress={() => run(undefined)}
        />
      </div>
    </>
  );
}

/** Method C — name, price, unit, save and keep going without leaving the screen. */
function RapidMethod({ prefill }: { prefill?: string }) {
  const navigate = useNavigate();
  const addProduct = usePos((s) => s.addProduct);
  const [name, setName] = useState(prefill ?? '');
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  // The shop's own vocabulary, not a fixed array. There used to be two lists —
  // one here and one in core — and they had already drifted apart.
  const units = usePos((s) => s.units);
  const categories = usePos((s) => s.categories);
  const addUnitOption = usePos((s) => s.addUnitOption);
  const addCategory = usePos((s) => s.addCategory);
  const lowStockDefault = usePos((s) => s.shop.lowStockDefault);

  const [unit, setUnit] = useState(units[0]?.name ?? 'ອັນ');
  const [cat, setCat] = useState(categories.at(-1) ?? 'ອື່ນໆ');
  const [opening, setOpening] = useState('');
  const [added, setAdded] = useState(0);
  const [adding, setAdding] = useState<'unit' | 'category' | null>(null);

  // parseKip, never Number(): a re-typed "55,000" would otherwise become 55.
  const priceValue = parseKip(price);
  const costValue = parseKip(cost);
  const openingValue = parseQty(opening);
  const valid = name.trim().length > 0 && priceValue !== null && priceValue > 0;

  const commit = useMemo(
    () => (input: NewProduct) => addProduct(input),
    [addProduct],
  );
  const { run, busy, error, dismiss } = useMoneyAction(commit, () => {
    setAdded((n) => n + 1);
    setName('');
    setPrice('');
    setCost('');
    setOpening('');
  });

  function save(andContinue: boolean) {
    if (!valid || priceValue === null) return;
    run({
      name: name.trim(),
      cat,
      unit,
      price: priceValue,
      // Cost is optional; without it the item is left out of the profit strip.
      ...(costValue !== null && costValue > 0 ? { cost: costValue } : {}),
      // Blank means "not stock-tracked", which the stock screen renders as a
      // legitimate state rather than missing data.
      ...(openingValue !== null ? { opening: openingValue } : {}),
    });
    if (!andContinue) navigate(-1);
  }

  return (
    <div className={cx('vscroll', styles.body)}>
      <ErrorBanner message={error} onDismiss={dismiss} />

      <Labelled label="ຊື່ສິນຄ້າ">
        <TextField value={name} onChange={setName} placeholder="ເຊັ່ນ ຊີມັງ ຕີໂກ້" label="ຊື່ສິນຄ້າ" />
      </Labelled>

      {/* Cost sits above the sell price at the shop owner's request: they enter
          what they paid, then price against it. Still optional to save — but the
          "· ບໍ່ບັງຄັບ" label is gone, because framing a field as skippable while
          putting it first contradicts itself. The margin line below the price is
          what makes the ordering earn its place. */}
      <Labelled label="ຕົ້ນທຶນ / ໜ່ວຍ (₭)">
        <TextField
          value={cost}
          onChange={setCost}
          kind="kip"
          placeholder="48000"
          label="ຕົ້ນທຶນຕໍ່ໜ່ວຍ"
        />
        <Text size={10} color={colors.textFaint}>
          ໃສ່ຕົ້ນທຶນເພື່ອໃຫ້ລາຍງານຄິດກຳໄລໄດ້
        </Text>
      </Labelled>

      <Labelled label="ລາຄາຂາຍ / ໜ່ວຍ (₭)">
        <TextField
          value={price}
          onChange={setPrice}
          kind="kip"
          placeholder="55000"
          label="ລາຄາຂາຍຕໍ່ໜ່ວຍ"
        />
        <MarginHint cost={costValue} price={priceValue} unit={unit} />
      </Labelled>

      <Labelled label="ໜ່ວຍຂາຍ">
        <div className={styles.units} role="radiogroup" aria-label="ໜ່ວຍຂາຍ">
          {units.map((option) => (
            <UnitChip
              key={option.name}
              unit={option.name}
              selected={option.name === unit}
              onPress={() => setUnit(option.name)}
            />
          ))}
          <button type="button" onClick={() => setAdding('unit')} className={styles.addChip}>
            <IconPlus size={13} />
          </button>
        </div>
      </Labelled>

      <Labelled label="ໝວດ">
        <div className={styles.units} role="radiogroup" aria-label="ໝວດ">
          {categories.map((option) => (
            <UnitChip
              key={option}
              unit={option}
              selected={option === cat}
              onPress={() => setCat(option)}
            />
          ))}
          <button type="button" onClick={() => setAdding('category')} className={styles.addChip}>
            <IconPlus size={13} />
          </button>
        </div>
      </Labelled>

      <Labelled label="ຈຳນວນທີ່ມີ · ບໍ່ບັງຄັບ">
        <TextField
          value={opening}
          onChange={setOpening}
          kind="qty"
          placeholder="120"
          label="ຈຳນວນທີ່ມີຢູ່"
        />
        <Text size={10} color={colors.textFaint}>
          {openingValue === null
            ? 'ປະຫວ່າງໄວ້ = ບໍ່ນັບສະຕັອກສິນຄ້ານີ້'
            : `ຈະເຕືອນເມື່ອເຫຼືອຕ່ຳກວ່າ ${lowStockDefault}`}
        </Text>
      </Labelled>

      <Button
        label="ບັນທຶກ ແລະ ຕໍ່ອັນຕໍ່ໄປ →"
        disabled={!valid || busy}
        onPress={() => save(true)}
      />
      <Button
        label="ບັນທຶກ ແລະ ອອກ"
        variant="outline"
        disabled={!valid || busy}
        onPress={() => save(false)}
      />

      {added > 0 ? (
        <Text size={11.5} color={colors.skyDeep} align="center">
          ເພີ່ມແລ້ວ {added} ລາຍການ
        </Text>
      ) : null}

      <AddValueSheet
        open={adding !== null}
        title={adding === 'unit' ? 'ເພີ່ມໜ່ວຍຂາຍ' : 'ເພີ່ມໝວດສິນຄ້າ'}
        placeholder={adding === 'unit' ? 'ເຊັ່ນ ແກັດ' : 'ເຊັ່ນ ສີທາ'}
        askFractional={adding === 'unit'}
        validate={(v) =>
          hasValue(adding === 'unit' ? units.map((u) => u.name) : categories, v)
            ? 'ມີຢູ່ແລ້ວ'
            : null
        }
        // Added and selected in one tap: the owner is mid-entry and adding the
        // value only to use it right now.
        onAdd={(value, fractional) => {
          if (adding === 'unit') {
            addUnitOption({ name: value, step: fractional ? 0.5 : 1 });
            setUnit(value);
          } else {
            addCategory(value);
            setCat(value);
          }
          setAdding(null);
        }}
        onClose={() => setAdding(null)}
      />
    </div>
  );
}

/**
 * Live margin under the sell price.
 *
 * Warns when the price is below cost but never blocks — a loss-leader and a
 * clearance price are both real decisions, and the shop knows its business
 * better than the form does.
 */
function MarginHint({
  cost,
  price,
  unit,
}: {
  cost: number | null;
  price: number | null;
  unit: string;
}) {
  if (cost === null || price === null || cost <= 0 || price <= 0) return null;

  const margin = price - cost;
  const pct = Math.round((margin / price) * 100);

  if (margin < 0) {
    return (
      <Text size={10} weight={700} color={colors.skyDeep}>
        ຂາຍຕ່ຳກວ່າຕົ້ນທຶນ {fmt(-margin)} ₭ / {unit}
      </Text>
    );
  }

  return (
    <Mono size={10} color={colors.textMuted}>
      ກຳໄລ {fmt(margin)} ₭ / {unit} · {pct}%
    </Mono>
  );
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.field}>
      <Text weight={700} size={11} color={colors.textMuted}>
        {label}
      </Text>
      {children}
    </div>
  );
}
